import React, { Suspense, lazy, useEffect } from 'react';
import Hero from './Hero';
import Trustbar from './Trustbar';
import About from './About';
import MiniConfigurator from './MiniConfigurator';
import ProductSuite from './ProductSuite';
import OtherFeatures1 from './OtherFeatures1';
import Contact from './Contact';
import Blog from './Blog';
import SEO from './SEO';
import SuccessBridge from './SuccessBridge';
import FinalCTA from './FinalCTA';

// Lazy load heavy components
const SuccessSpotlight = lazy(() => import('./SuccessSpotlight'));

const SectionSkeleton = () => (
    <div className="w-full py-20 bg-slate-50 animate-pulse">
        <div className="container-xl">
            <div className="h-8 w-64 bg-slate-200 rounded-full mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-12">
                <div className="h-[400px] bg-slate-200 rounded-[3rem]"></div>
                <div className="space-y-4">
                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                    <div className="h-4 w-5/6 bg-slate-200 rounded"></div>
                    <div className="h-4 w-4/6 bg-slate-200 rounded"></div>
                </div>
            </div>
        </div>
    </div>
);

interface LandingPageProps {
    onBookDemo: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onBookDemo }) => {
    useEffect(() => {
        // 1. Scroll-based reveal animations
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.12
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                }
            });
        }, observerOptions);

        const animatedElements = document.querySelectorAll('[data-animation]');
        animatedElements.forEach(el => observer.observe(el));

        // 2. Hash-based scrolling on mount (for links from other pages)
        if (window.location.hash) {
            setTimeout(() => {
                const targetId = window.location.hash.substring(1);
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    const headerOffset = 90;
                    const elementPosition = targetElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.scrollY - headerOffset;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 500);
        }

        // 3. Smooth scroll delegation for internal links
        const handleInternalScroll = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            if (link && link.hash && link.origin === window.location.origin && link.pathname === window.location.pathname) {
                e.preventDefault();
                const targetId = link.hash.substring(1);
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    const headerOffset = 90;
                    const elementPosition = targetElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.scrollY - headerOffset;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }
        };

        document.addEventListener('click', handleInternalScroll);

        return () => {
            observer.disconnect();
            document.removeEventListener('click', handleInternalScroll);
        };
    }, []);

    return (
        <>
            <SEO
                title="Powering High-Growth Furniture Retail"
                description="India's leading 3D visualization & AR platform for furniture retailers. Trusted by 40+ stores. Benefit from 94% higher conversion rates and 40% fewer returns."
            />
            <Hero onBookDemo={onBookDemo} />
            <Trustbar />
            <About />
            <ProductSuite onBookDemo={onBookDemo} />
            <MiniConfigurator onBookDemo={onBookDemo} />
            <OtherFeatures1 />
            <SuccessBridge />
            <Suspense fallback={<SectionSkeleton />}>
                <SuccessSpotlight onBookDemo={onBookDemo} />
            </Suspense>
            <FinalCTA onBookDemo={onBookDemo} />
            <Contact onBookDemo={onBookDemo} />
            <Blog />
        </>
    );
};

export default LandingPage;
